// Toast queue (IMPLEMENTATION §2.6 "Toasts", R13, UX §2 Toasts / §7). PURE: at most `max` toasts show (the
// newest win), each lives until its deadline; an undo toast replaces an older undo toast (the sim keeps only
// the last sale, R13); a one-time toast is keyed so it never shows twice in one list. While a finger is down
// on a drag the clock stops ("undo toasts pause while a finger is down", UX §7): `pause` / `resume` shift the
// deadlines by the time paused.

export type ToastKind = 'undo' | 'info' | 'unlock';

export interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
  /** Wall ms when it goes away. */
  until: number;
  /** undo: the sale token for `undoSell`. */
  token?: number;
  /** Dedupe key (one-time toasts, unlock toasts). */
  key?: string;
}

export interface ToastState {
  list: Toast[];
  nextId: number;
  /** Wall ms the clock stopped at (a drag runs), else null. */
  pausedAt: number | null;
}

export const NO_TOASTS: ToastState = { list: [], nextId: 1, pausedAt: null };

export interface ToastInput {
  kind: ToastKind;
  text: string;
  ms: number;
  token?: number;
  key?: string;
}

/** Adds a toast (newest last); older ones beyond `max` go, an older undo toast is replaced. */
export function pushToast(st: ToastState, t: ToastInput, now: number, max: number): ToastState {
  if (t.key !== undefined && st.list.some((x) => x.key === t.key)) return st;
  const toast: Toast = {
    id: st.nextId,
    kind: t.kind,
    text: t.text,
    until: (st.pausedAt ?? now) + t.ms,
    ...(t.token !== undefined ? { token: t.token } : {}),
    ...(t.key !== undefined ? { key: t.key } : {}),
  };
  let list = st.list.filter((x) => !(t.kind === 'undo' && x.kind === 'undo'));
  list = [...list, toast];
  if (list.length > max) list = list.slice(list.length - max);
  return { ...st, list, nextId: st.nextId + 1 };
}

/** Drops expired toasts (none while paused). */
export function expireToasts(st: ToastState, now: number): ToastState {
  if (st.pausedAt !== null) return st;
  const list = st.list.filter((x) => x.until > now);
  return list.length === st.list.length ? st : { ...st, list };
}

export function removeToast(st: ToastState, id: number): ToastState {
  const list = st.list.filter((x) => x.id !== id);
  return list.length === st.list.length ? st : { ...st, list };
}

export function pauseToasts(st: ToastState, now: number): ToastState {
  return st.pausedAt !== null ? st : { ...st, pausedAt: now };
}

export function resumeToasts(st: ToastState, now: number): ToastState {
  if (st.pausedAt === null) return st;
  const dt = Math.max(0, now - st.pausedAt);
  return { ...st, pausedAt: null, list: st.list.map((x) => ({ ...x, until: x.until + dt })) };
}

/** The next deadline (for a timer), or null. */
export function nextExpiry(st: ToastState): number | null {
  if (st.pausedAt !== null || st.list.length === 0) return null;
  let m = Number.POSITIVE_INFINITY;
  for (const t of st.list) m = Math.min(m, t.until);
  return m;
}
